import fetcher from '../lib/fetcher'
import {ClientUserStatsSchema} from '../schemas/user.schemas'
import {
  ERole,
  TActivationFilter,
  TClientUserStats,
  TInviteUser,
  TSortOrder,
  TUpdateUser,
  TUser,
  TUserSortBy,
  TUsersPaginated,
} from '../types/user.type'

export type TGetUsersOptions = {
  search?: string
  sortBy?: TUserSortBy
  sortOrder?: TSortOrder
  roles?: ERole[]
  activation?: TActivationFilter
}

const getUserById = async (userId: string | null) => {
  const response = await fetcher.get<TUser>(`/api/user/${userId}`)
  return response.data
}

const getUsers = async (
  clientId: string,
  page: number,
  perPage: number,
  options: TGetUsersOptions = {},
) => {
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage),
  })
  if (options.search) params.set('search', options.search)
  if (options.sortBy) params.set('sortBy', options.sortBy)
  if (options.sortOrder) params.set('sortOrder', options.sortOrder)
  if (options.roles?.length) params.set('roles', options.roles.join(','))
  if (options.activation) params.set('activation', options.activation)

  const response = await fetcher.get<TUsersPaginated>(
    `/api/user/client/${clientId}?${params.toString()}`,
  )
  return response.data
}

/**
 * Roster for the activity-list requestor filter: every client user who can
 * create/request activities, i.e. everyone except VIEWER.
 */
const getRequestorOptions = async (clientId: string): Promise<TUser[]> => {
  const response = await getUsers(clientId, 1, -1, {
    roles: [ERole.REQUESTOR, ERole.OPC, ERole.ADMIN],
    sortBy: 'name',
    sortOrder: 'asc',
  })
  return response?.data ?? []
}

const getClientUserStats = async (
  clientId: string,
): Promise<TClientUserStats> => {
  const response = await fetcher.get<TClientUserStats>(
    `/api/user/client/${clientId}/stats`,
  )
  return ClientUserStatsSchema.parse(response.data)
}

const createUser = async (userData: TInviteUser) => {
  return fetcher.post('/api/user/create', userData)
}

const updateUser = async (userId: string, userData: TUpdateUser) => {
  return fetcher.put(`/api/user/${userId}`, userData)
}

const deleteUser = async (userId: string, clientId: string) => {
  return fetcher.delWithBody(`/api/user/${userId}`, {clientId})
}

const resendInvitation = async (userId: string, clientId: string) => {
  return fetcher.post(`/api/user/${userId}/resend-invitation`, {clientId})
}

export const UserService = {
  getUserById,
  getUsers,
  getRequestorOptions,
  getClientUserStats,
  createUser,
  updateUser,
  deleteUser,
  resendInvitation,
}
